/**
 * Status Utilities for ParkPro Frontend
 * 
 * Provides consistent status handling, colors, and labels across the UI
 */

// Client Relationship Statuses
export const CLIENT_STATUS = {
  LEAD: 'lead',
  PROSPECT: 'prospect',
  CUSTOMER: 'customer',
  INACTIVE: 'inactive'
};

export const CLIENT_STATUS_INFO = {
  [CLIENT_STATUS.LEAD]: {
    label: 'Lead',
    description: 'Initial contact, not yet engaged',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: 'UserPlus',
    order: 1
  },
  [CLIENT_STATUS.PROSPECT]: {
    label: 'Prospect',
    description: 'Engaged and interested',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: 'UserCheck',
    order: 2
  },
  [CLIENT_STATUS.CUSTOMER]: {
    label: 'Customer',
    description: 'Active or past customer',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: 'Heart',
    order: 3
  },
  [CLIENT_STATUS.INACTIVE]: {
    label: 'Inactive',
    description: 'No longer engaged',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: 'UserX',
    order: 4
  }
};

// Trip Statuses
export const TRIP_STATUS = {
  PENDING: 'pending',           // Trip created, awaiting questionnaire
  PLANNING: 'planning',         // Questionnaire submitted, itinerary being generated
  CONFIRMED: 'confirmed',       // Itinerary generated, trip confirmed
  ACTIVE: 'active',             // Client is currently on the trip
  COMPLETED: 'completed',       // Trip has finished
  CANCELLED: 'cancelled'        // Trip was cancelled
};

export const TRIP_STATUS_INFO = {
  [TRIP_STATUS.PENDING]: {
    label: 'Pending',
    description: 'Awaiting questionnaire completion',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: 'Clock',
    order: 1
  },
  [TRIP_STATUS.PLANNING]: {
    label: 'Planning',
    description: 'Questionnaire completed, generating itinerary',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: 'ClipboardList',
    order: 2
  },
  [TRIP_STATUS.CONFIRMED]: {
    label: 'Confirmed',
    description: 'Itinerary ready, awaiting trip start',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: 'CheckCircle',
    order: 3
  },
  [TRIP_STATUS.ACTIVE]: {
    label: 'Active',
    description: 'Client is currently on the trip',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: 'Plane',
    order: 4
  },
  [TRIP_STATUS.COMPLETED]: {
    label: 'Completed',
    description: 'Trip has finished successfully',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: 'CheckCircle2',
    order: 5
  },
  [TRIP_STATUS.CANCELLED]: {
    label: 'Cancelled',
    description: 'Trip was cancelled',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: 'XCircle',
    order: 6
  }
};

/**
 * Get status information for a trip
 * @param {string} status - Trip status
 * @returns {Object} - Status info object
 */
export function getTripStatusInfo(status) {
  const normalizedStatus = status?.toLowerCase();
  return TRIP_STATUS_INFO[normalizedStatus] || TRIP_STATUS_INFO[TRIP_STATUS.PENDING];
}

/**
 * Get status information for a client
 * @param {string} status - Client relationship status
 * @returns {Object} - Status info object
 */
export function getClientStatusInfo(status) {
  const normalizedStatus = status?.toLowerCase();
  return CLIENT_STATUS_INFO[normalizedStatus] || CLIENT_STATUS_INFO[CLIENT_STATUS.LEAD];
}

/**
 * Get CSS classes for trip status badge
 * @param {string} status - Trip status
 * @returns {string} - CSS classes
 */
export function getTripStatusClasses(status) {
  const info = getTripStatusInfo(status);
  return `px-3 py-1 text-xs font-medium rounded-full border ${info.color}`;
}

/**
 * Get CSS classes for client status badge
 * @param {string} status - Client relationship status
 * @returns {string} - CSS classes
 */
export function getClientStatusClasses(status) {
  const info = getClientStatusInfo(status);
  return `px-3 py-1 text-xs font-medium rounded-full border ${info.color}`;
}

/**
 * Check if trip is viewable (has itinerary)
 * @param {string} status - Trip status
 * @returns {boolean} - True if trip can be viewed
 */
export function isTripViewable(status) {
  const normalizedStatus = status?.toLowerCase();
  return [
    TRIP_STATUS.CONFIRMED,
    TRIP_STATUS.ACTIVE,
    TRIP_STATUS.COMPLETED
  ].includes(normalizedStatus);
}

/**
 * Check if trip is editable
 * @param {string} status - Trip status
 * @returns {boolean} - True if trip can be edited
 */
export function isTripEditable(status) {
  const normalizedStatus = status?.toLowerCase();
  return ![
    TRIP_STATUS.COMPLETED,
    TRIP_STATUS.CANCELLED
  ].includes(normalizedStatus);
}

/**
 * Get human-readable status label
 * @param {string} status - Status value
 * @param {string} type - 'trip' or 'client'
 * @returns {string} - Human-readable label
 */
export function getStatusLabel(status, type = 'trip') {
  if (type === 'client') {
    return getClientStatusInfo(status).label;
  }
  return getTripStatusInfo(status).label;
}

/**
 * Calculate trip status based on dates (client-side estimation)
 * @param {string} startDate - Trip start date
 * @param {string} endDate - Trip end date
 * @param {string} currentStatus - Current trip status
 * @returns {string} - Estimated status
 */
export function estimateTripStatus(startDate, endDate, currentStatus) {
  if (!startDate || !endDate) {
    return currentStatus || TRIP_STATUS.PENDING;
  }
  
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  
  // If trip has ended
  if (now > end) {
    return TRIP_STATUS.COMPLETED;
  }
  
  // If trip is currently happening
  if (now >= start && now <= end) {
    return TRIP_STATUS.ACTIVE;
  }
  
  // If trip hasn't started yet and has itinerary
  if (now < start && isTripViewable(currentStatus)) {
    return TRIP_STATUS.CONFIRMED;
  }
  
  return currentStatus || TRIP_STATUS.PENDING;
}

/**
 * Get status progress percentage (for progress bars)
 * @param {string} status - Trip status
 * @returns {number} - Progress percentage (0-100)
 */
export function getStatusProgress(status) {
  const normalizedStatus = status?.toLowerCase();
  const progressMap = {
    [TRIP_STATUS.PENDING]: 20,
    [TRIP_STATUS.PLANNING]: 40,
    [TRIP_STATUS.CONFIRMED]: 60,
    [TRIP_STATUS.ACTIVE]: 80,
    [TRIP_STATUS.COMPLETED]: 100,
    [TRIP_STATUS.CANCELLED]: 0
  };
  
  return progressMap[normalizedStatus] || 0;
}

/**
 * Get next possible statuses for a trip
 * @param {string} currentStatus - Current trip status
 * @returns {Array} - Array of possible next statuses
 */
export function getNextPossibleStatuses(currentStatus) {
  const normalizedStatus = currentStatus?.toLowerCase();
  const transitionsMap = {
    [TRIP_STATUS.PENDING]: [TRIP_STATUS.PLANNING, TRIP_STATUS.CANCELLED],
    [TRIP_STATUS.PLANNING]: [TRIP_STATUS.CONFIRMED, TRIP_STATUS.CANCELLED],
    [TRIP_STATUS.CONFIRMED]: [TRIP_STATUS.ACTIVE, TRIP_STATUS.CANCELLED],
    [TRIP_STATUS.ACTIVE]: [TRIP_STATUS.COMPLETED],
    [TRIP_STATUS.COMPLETED]: [],
    [TRIP_STATUS.CANCELLED]: []
  };
  
  return transitionsMap[normalizedStatus] || [];
}

export default {
  CLIENT_STATUS,
  CLIENT_STATUS_INFO,
  TRIP_STATUS,
  TRIP_STATUS_INFO,
  getTripStatusInfo,
  getClientStatusInfo,
  getTripStatusClasses,
  getClientStatusClasses,
  isTripViewable,
  isTripEditable,
  getStatusLabel,
  estimateTripStatus,
  getStatusProgress,
  getNextPossibleStatuses
};

